import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { trackEvent, setUserProperties } from '@/lib/posthog';
import { activationEvents, ACTIVATION_CRITERIA, newlyCompletedActions } from '@/lib/analytics/events/activation';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { logger } from '@/lib/logger';
import type { ActivationAction } from '@/lib/analytics/events/activation';

const ACTIVATION_STORAGE_KEY = 'settlewise-activation-fired';
const ACTIVATION_ACTIONS_KEY = 'settlewise-activation-actions';

/**
 * Checks whether the current user meets the activation criteria and fires
 * the `user_activated` PostHog event exactly once.
 *
 * Activation criteria (v1):
 *   1. User has viewed their strategy
 *   2. User has used at least one secondary feature:
 *      - Sent a Sage message
 *      - Uploaded a document
 *      - Used the support calculator
 *
 * The hook queries Supabase for evidence of each action and stores a
 * localStorage flag so the event fires at most once per user/device.
 *
 * This runs passively in AppLayout — no user-visible side effects.
 */
export function useActivationTracking(): { isActivated: boolean } {
  const { user } = useAuth();
  const [isActivated, setIsActivated] = useState(false);
  const checkedRef = useRef(false);

  const checkActivation = useCallback(async (userId: string) => {
    // Already fired for this user on this device
    const storageKey = `${ACTIVATION_STORAGE_KEY}-${userId}`;
    if (localStorage.getItem(storageKey) === 'true') {
      setIsActivated(true);
      return;
    }

    try {
      const completedActions: ActivationAction[] = [];

      // Check 1: Has the user viewed a strategy? (strategy record exists)
      const { data: strategies, error: strategyError } = await supabase
        .from('strategies')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (strategyError) {
        trackSupabaseError('select', 'strategies', strategyError);
        return;
      }

      if (strategies && strategies.length > 0) {
        completedActions.push('strategy_viewed');
      }

      // If required action not met, no need to check secondary actions
      if (!completedActions.includes(ACTIVATION_CRITERIA.requiredAction)) {
        return;
      }

      // Check 2a: Has the user sent a Sage message?
      const { data: sageMessages, error: sageError } = await supabase
        .from('sage_conversations')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (sageError) {
        trackSupabaseError('select', 'sage_conversations', sageError);
      } else if (sageMessages && sageMessages.length > 0) {
        completedActions.push('sage_message_sent');
      }

      // Check 2b: Has the user uploaded a document?
      const { data: documents, error: docError } = await supabase
        .from('uploaded_documents')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (docError) {
        trackSupabaseError('select', 'uploaded_documents', docError);
      } else if (documents && documents.length > 0) {
        completedActions.push('document_uploaded');
      }

      // Check 2c: Has the user used the support calculator?
      const { data: supportCalcs, error: supportError } = await supabase
        .from('support_calculations')
        .select('id')
        .eq('user_id', userId)
        .limit(1);

      if (supportError) {
        trackSupabaseError('select', 'support_calculations', supportError);
      } else if (supportCalcs && supportCalcs.length > 0) {
        completedActions.push('support_calculated');
      }

      // Fire activation_action_completed once per newly-completed action.
      const actionsKey = `${ACTIVATION_ACTIONS_KEY}-${userId}`;
      let firedActions: ActivationAction[] = [];
      try {
        firedActions = JSON.parse(localStorage.getItem(actionsKey) || '[]');
      } catch {
        firedActions = [];
      }
      const newActions = newlyCompletedActions(completedActions, firedActions);
      for (const action of newActions) {
        const actionEvent = activationEvents.activationActionCompleted(action);
        trackEvent(actionEvent.event, actionEvent.properties);
      }
      if (newActions.length > 0) {
        localStorage.setItem(actionsKey, JSON.stringify([...firedActions, ...newActions]));
      }

      // Evaluate: required action + at least one secondary action
      const hasRequired = completedActions.includes(ACTIVATION_CRITERIA.requiredAction);
      const hasSecondary = ACTIVATION_CRITERIA.secondaryActions.some(
        (action) => completedActions.includes(action)
      );

      if (hasRequired && hasSecondary) {
        // Fire the activation event
        const eventData = activationEvents.userActivated(completedActions);
        trackEvent(eventData.event, eventData.properties);

        // Set user property for segmentation
        setUserProperties({
          is_activated: true,
          activated_at: new Date().toISOString(),
          activation_actions: completedActions,
        });

        // Persist so we never fire again for this user
        localStorage.setItem(storageKey, 'true');
        setIsActivated(true);

        logger.log('[Activation] User activated with actions:', completedActions);
      }
    } catch (err) {
      // Non-critical — swallow errors silently
      logger.error('[Activation] Check failed:', err);
    }
  }, []);

  useEffect(() => {
    if (!user?.id || checkedRef.current) return;
    checkedRef.current = true;

    // Delay the check slightly so it doesn't compete with initial page load queries
    const timer = setTimeout(() => {
      checkActivation(user.id);
    }, 3000);

    return () => clearTimeout(timer);
  }, [user?.id, checkActivation]);

  return { isActivated };
}
